import React, {useCallback, useMemo} from 'react';

import {DateInput} from 'src/components/input';
import {LimitItem} from 'src/components/limit-item/limit-item';
import {AccountLicenseLink} from 'src/components/links';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {MAX_NUMBER_VALUE} from 'src/constants';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useLicenseCommonLimits} from 'src/hooks/dictionaries';
import {useUserLicense} from 'src/modules/profile/account/create-user/steps/user-limits/useUserLicense';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';

import {ResetUserDefaultLimits} from '../components/reset-user-default-limits';

import {UserCommonLimitsSkeleton} from './user-common-limits.skeleton';

import type {TempLimitsSettings} from '../typedef';
import type {Dispatch, SetStateAction} from 'react';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {CheckBoxChangeData} from 'src/typedef';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    disabled?: boolean,

    usedLimits?: Limit[],
    licenseLimits: Limit[] | null,
    commonLimits: Limit[],
    setCommonLimits: Dispatch<SetStateAction<Limit[]>>,
    tempLimitsSettings: TempLimitsSettings,
    setTempLimitsSettings: Dispatch<SetStateAction<TempLimitsSettings>>,
    onFieldChange: (fieldName: string) => void,
};

export const UserCommonLimitsView = (props: Props) => {
    const {
        isLoading,
        loadingError,
        disabled,
        onFieldChange,

        usedLimits,
        licenseLimits,
        commonLimits,
        setCommonLimits,
        tempLimitsSettings,
        setTempLimitsSettings,
    } = props;

    const {getTodayString, today} = useDateTime();
    const activeLicense = useUserLicense();
    const {licenseCommonLimits} = useLicenseCommonLimits();

    const changeCommonLimit = useCallback((id: string, newValue?: number) => {
        if (id) {
            const defaultValue = licenseLimits?.find(({limitId}) => (
                id === limitId
            ))?.value;

            setCommonLimits(prevState => {
                if (prevState.find(e => e.limitId === id)?.value !== newValue) {
                    onFieldChange('Common: ' + id);
                }

                const unlimited = newValue === undefined;
                const value = unlimited ? MAX_NUMBER_VALUE : newValue;

                return [
                    ...prevState.filter(({limitId}) => id !== limitId),
                    ...defaultValue === value ? [] : [{limitId: id, value, unlimited}],
                ];
            });
        }
    }, [licenseLimits, setCommonLimits, onFieldChange]);

    const clearCustomLimit = useCallback((id: string) => {
        setCommonLimits(prevState => {
            onFieldChange('Common: ' + id);
            return prevState.filter(({limitId}) => id !== limitId);
        });
    }, [setCommonLimits, onFieldChange]);

    const changeTemporaryLimit = useCallback((id: string, newValue?: number) => {
        if (id) {
            setTempLimitsSettings(prevState => {
                if (prevState.limits.find(({limitId}) => id === limitId)?.value !== newValue) {
                    onFieldChange('Temp: ' + id);
                }

                const {expiresOn} = prevState;
                const unlimited = newValue === undefined;
                const value = unlimited ? MAX_NUMBER_VALUE : newValue;

                return {
                    ...prevState,
                    limits: [
                        ...prevState.limits.filter(({limitId}) => id !== limitId),
                        {
                            limitId: id,
                            value: unlimited ? MAX_NUMBER_VALUE : value as number,
                            temporaryDate: expiresOn || '',
                            unlimited,
                        },
                    ],
                };
            });
        }
    }, [setTempLimitsSettings, onFieldChange]);

    const clearTemporaryLimit = useCallback((id: string) => {
        setTempLimitsSettings(prevState => {
            onFieldChange('Temp: ' + id);

            return {
                ...prevState,
                limits: prevState.limits.filter(({limitId}) => id !== limitId),
            };
        });
    }, [setTempLimitsSettings, onFieldChange]);

    const changeTemporaryLimitMode = useCallback((changeData: CheckBoxChangeData) => {
        setTempLimitsSettings({
            limits: [],
            expiresOn: changeData.event.currentTarget.checked
                ? getTodayString()
                : '',
        });
    }, [getTodayString, setTempLimitsSettings]);

    const resetLimits = useCallback(() => {
        setCommonLimits([]);
        setTempLimitsSettings({expiresOn: '', limits: []});
    }, [setCommonLimits, setTempLimitsSettings]);

    const licenseLink = useMemo(() => {
        if (!activeLicense) {
            return null;
        }

        if (activeLicense.accountId && activeLicense.typeCode && activeLicense.licenseId) {
            return (
                <AccountLicenseLink
                    text="account's"
                    accountId={activeLicense.accountId}
                    licenseId={activeLicense.licenseId}
                    licenseType={activeLicense.typeCode}
                    place="Views and downloads limits"
                    blank={true}
                />
            );
        } else {
            return null;
        }
    }, [activeLicense]);

    return (
        <Skeleton shown={isLoading} view={<UserCommonLimitsSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                accessRight={ACCESS_RIGHTS.USER_LIMITS_VIEW as AccessRight}>
                <FormSection headline="Views and downloads">
                    <Group direction="v">
                        {activeLicense ? (
                            <ResetUserDefaultLimits
                                licenseLink={licenseLink}
                                isDefaultUsageLimits={!commonLimits.length && tempLimitsSettings.expiresOn === ''}
                                loading={isLoading}
                                defaultUsageLimits={licenseLimits}
                                disabled={disabled}
                                onReset={resetLimits}
                            />
                        ) : null}
                        <Group>
                            <CheckBox
                                label="Make changes temporary"
                                disabled={disabled}
                                checked={!!tempLimitsSettings.expiresOn}
                                onCheckBoxChange={changeTemporaryLimitMode}
                            />
                            <CustomGroupItem label="Until">
                                <DateInput
                                    id="temporary-limits-until-date-picker"
                                    clearable={true}
                                    width={270}
                                    selectedDate={tempLimitsSettings.expiresOn}
                                    onChange={expiresOn => {
                                        onFieldChange('Until');
                                        setTempLimitsSettings(prevState => ({
                                            ...prevState,
                                            limits: expiresOn ? prevState.limits.map(limit => ({
                                                ...limit,
                                                temporaryDate: expiresOn,
                                            })) : [],
                                            expiresOn,
                                        }));
                                    }}
                                    minDate={today}
                                    readonly={disabled}
                                />
                            </CustomGroupItem>
                        </Group>
                        <ResponsiveElementGroup>
                            {licenseCommonLimits.map(({description, children}) => (
                                <WidthItem key={description} width={300}>
                                    <Group direction="v" gap="s">
                                        <Label>{description}</Label>
                                        {children?.map(limit => {
                                            const defaultValue = licenseLimits?.find(({limitId}) => (
                                                limit.code === limitId
                                            ))?.value || 0;

                                            const customValue = commonLimits.find(({limitId}) => (
                                                limitId === limit.code
                                            ))?.value;

                                            const temporaryValue = tempLimitsSettings.limits.find(({limitId}) => (
                                                limitId === limit.code
                                            ))?.value;

                                            const usedValue = usedLimits?.find(({limitId}) => (
                                                limitId === limit.code
                                            ))?.value;

                                            return (
                                                <LimitItem
                                                    key={limit.code}
                                                    limitId={limit.code}
                                                    readonly={disabled}
                                                    limitDictionary={limit}
                                                    defaultValue={defaultValue}
                                                    customValue={customValue}
                                                    tempValue={temporaryValue}
                                                    untilDate={tempLimitsSettings.expiresOn}
                                                    usedValue={usedValue}
                                                    onClearCustomLimit={clearCustomLimit}
                                                    onClearTemporaryLimit={clearTemporaryLimit}
                                                    onChangeLimit={tempLimitsSettings.expiresOn
                                                        ? changeTemporaryLimit
                                                        : changeCommonLimit}
                                                />
                                            );
                                        })}
                                    </Group>
                                </WidthItem>
                            ))}
                        </ResponsiveElementGroup>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
